// admin-client/src/components/MonthPicker.jsx
// =============================================================================
// Compact YYYY-MM picker with prev / next arrow buttons. Used on the Stats and
// Cross-check pages so admins can flip through months without typing.
//
//   <MonthPicker value={month} onChange={setMonth} />
//
// Helpers exported alongside:
//   - prevMonthOfNow() → "YYYY-MM" string for the previous calendar month
//     (the sensible default for analytics pages, since the current month
//      isn't finalised yet).
//   - shiftMonth(label, delta) → adjacent month string.
// =============================================================================

function clampMonth(yr, mo /* 0-indexed */) {
  // mo can be -1..12; normalise.
  const d = new Date(yr, mo, 1);
  return { y: d.getFullYear(), m: d.getMonth() }; // m still 0-indexed
}

export function shiftMonth(label, delta) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(label || ''));
  if (!m) return label;
  const { y, m: mo } = clampMonth(parseInt(m[1], 10), parseInt(m[2], 10) - 1 + delta);
  return `${y}-${String(mo + 1).padStart(2, '0')}`;
}

export function prevMonthOfNow() {
  const t = new Date();
  return shiftMonth(`${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}`, -1);
}

export function thisMonth() {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}`;
}

export default function MonthPicker({ value, onChange, label = 'Month', className = '' }) {
  const next = shiftMonth(value, 1);
  // Don't let the user nudge past "this month" — there's no data to show.
  const atOrPastNow = next > thisMonth();
  return (
    <label className={'block text-sm ' + className}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 inline-flex items-stretch border border-slate-300 rounded overflow-hidden">
        <button
          type="button"
          onClick={() => onChange(shiftMonth(value, -1))}
          className="px-2 hover:bg-slate-100 text-slate-600"
          title="Previous month"
        >
          ←
        </button>
        <input
          pattern="\d{4}-\d{2}"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="px-2 py-1.5 text-sm font-mono w-24 text-center outline-none"
        />
        <button
          type="button"
          onClick={() => onChange(next)}
          disabled={atOrPastNow}
          className="px-2 hover:bg-slate-100 text-slate-600 disabled:opacity-30 disabled:hover:bg-transparent"
          title={atOrPastNow ? 'No future months' : 'Next month'}
        >
          →
        </button>
      </div>
    </label>
  );
}
